import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  INCIDENT_GRAPHQL_DETAIL,
  INCIDENT_LIST_GROUP_SUMMARY,
  INCIDENT_LIST_PAGED,
  INCIDENT_V2_TICKETS,
} from '@elementary/common-ui/constants/queryKeys';
import { INCIDENT_ID } from '@elementary/common-ui/constants/urlParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useCreateTicket from 'incidents/hooks/useCreateTicket';
import useSnackbar from 'shared/hooks/useSnackbar';
import { useTicketingIntegration } from 'shared/hooks/integrations';
import type { TicketSelection } from 'shared/hooks/useTicketSearch';
import { attachIncidentV2Ticket, detachIncidentV2Ticket } from '../api';
import type { IncidentStatus } from '../types';

type Props = {
  incidentId: string;
  status: IncidentStatus;
};

const useIncidentV2Ticketing = ({ incidentId, status }: Props) => {
  const selectedEnvId = useSelectedEnv();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { ticketingQuery } = useTicketingIntegration({
    envId: selectedEnvId,
  });

  const enabledIntegrations = useMemo(
    () =>
      ticketingQuery?.data?.integrations.filter(
        (integration) => integration.enabled,
      ),
    [ticketingQuery?.data?.integrations],
  );

  const incidentUrl = selectedEnvId
    ? `${window.location.origin}/${selectedEnvId}/incidents?${INCIDENT_ID}=${incidentId}`
    : '';

  const {
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
  } = useCreateTicket({
    integrations: enabledIntegrations,
    incidentUrl,
    incidentId,
  });

  const { mutateAsync: attachTicket, isLoading: isAttaching } = useMutation({
    mutationFn: async ({
      ticketingIntegrationId,
      ticket,
    }: {
      ticketingIntegrationId: string;
      ticket: TicketSelection;
    }) => {
      if (!selectedEnvId) {
        throw new Error('Environment not found');
      }
      return attachIncidentV2Ticket(
        selectedEnvId,
        incidentId,
        ticketingIntegrationId,
        ticket.key,
        ticket.url,
      );
    },
    onSuccess: (_data, { ticket }) => {
      if (!selectedEnvId) return;
      [
        [INCIDENT_V2_TICKETS, selectedEnvId, incidentId],
        [INCIDENT_GRAPHQL_DETAIL, incidentId, selectedEnvId],
        [INCIDENT_LIST_PAGED],
        [INCIDENT_LIST_GROUP_SUMMARY],
      ].forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey, { exact: false });
      });
      openSnackbar({
        variant: 'success',
        message: 'Ticket linked',
        actions: [
          {
            text: 'View ticket',
            cb: () => window.open(ticket.url, '_blank'),
          },
        ],
      });
    },
    onError: () => {
      openSnackbar({
        variant: 'error',
        message: 'Failed to link ticket',
      });
    },
  });

  const { mutateAsync: unlinkTicket, isLoading: isUnlinking } = useMutation({
    mutationFn: async (serviceTicketId: string) => {
      if (!selectedEnvId) {
        throw new Error('Environment not found');
      }
      return detachIncidentV2Ticket(selectedEnvId, incidentId, serviceTicketId);
    },
    onSuccess: () => {
      if (!selectedEnvId) return;
      [
        [INCIDENT_V2_TICKETS, selectedEnvId, incidentId],
        [INCIDENT_GRAPHQL_DETAIL, incidentId, selectedEnvId],
        [INCIDENT_LIST_PAGED],
        [INCIDENT_LIST_GROUP_SUMMARY],
      ].forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey, { exact: false });
      });
      openSnackbar({
        variant: 'success',
        message: 'Ticket unlinked',
      });
    },
    onError: () => {
      openSnackbar({
        variant: 'error',
        message: 'Failed to unlink ticket',
      });
    },
  });

  const handleViewTicket = (url: string) => {
    window.open(url, '_blank');
  };

  const isResolved = status === 'RESOLVED';

  return {
    selectedEnvId,
    enabledIntegrations,
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
    handleViewTicket,
    incidentUrl,
    isResolved,
    attachTicket,
    isAttaching,
    unlinkTicket,
    isUnlinking,
  };
};

export default useIncidentV2Ticketing;
